// v641.1: Notification Center — bell icon with badge + dropdown.
// Aggregates mail, bounty, market and guild events from /api/notifications.
import React, { useEffect, useRef, useState, useCallback } from 'react';

const POLL_MS = 60_000; // refresh every minute while open

const NotificationCenter = ({
  onOpenMessages,
  onOpenBounties,
  onOpenMarket,
  onOpenGuild,
}) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ total: 0, counts: {}, items: [] });
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  // v641.1 bugfix: monotonic request id so an older in-flight fetch can't
  // overwrite the result of a newer one (e.g., spam-clicking the bell).
  const reqSeqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeqRef.current;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/notifications', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (seq !== reqSeqRef.current) return; // a newer request is in flight
      if (json.success) {
        setData({ total: json.total || 0, counts: json.counts || {}, items: json.items || [] });
      }
    } catch (e) {
      // silent
    } finally {
      if (seq === reqSeqRef.current) setLoading(false);
    }
  }, []);

  // Initial load + visible-tab interval
  useEffect(() => {
    load();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Refresh on open
  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Click-outside close
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleClickItem = (item) => {
    setOpen(false);
    if (item.link === 'messages' && onOpenMessages) onOpenMessages();
    else if (item.link === 'bounties' && onOpenBounties) onOpenBounties();
    else if (item.link === 'market' && onOpenMarket) onOpenMarket();
    else if (item.link === 'guild' && onOpenGuild) onOpenGuild();
  };

  const fmtAgo = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const ms = Date.now() - d.getTime();
    if (ms < 0) {
      // future (e.g., expires_at) — render as countdown
      const sec = Math.abs(ms) / 1000;
      if (sec < 3600) return `${Math.round(sec / 60)}m`;
      if (sec < 86400) return `${Math.round(sec / 3600)}h`;
      return `${Math.round(sec / 86400)}d`;
    }
    const sec = ms / 1000;
    if (sec < 60) return 'just now';
    if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
    return `${Math.round(sec / 86400)}d ago`;
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <button
        className="dm-nav-btn"
        onClick={() => setOpen(o => !o)}
        title={`Notifications${data.total > 0 ? ` (${data.total})` : ''}`}
        style={{ position: 'relative' }}
      >
        🔔
        {data.total > 0 && (
          <span className="dm-nav-badge" style={{ background: '#ef4444' }}>
            {data.total > 99 ? '99+' : data.total}
          </span>
        )}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 6,
            width: 320,
            maxHeight: 420,
            overflowY: 'auto',
            background: 'rgba(15, 15, 22, 0.98)',
            border: '1px solid #c9a84c44',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
            zIndex: 9999,
            padding: 6,
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 8px',
            borderBottom: '1px solid #2a2a32',
            marginBottom: 4,
          }}>
            <span style={{ color: '#c9a84c', fontSize: 13, fontWeight: 600 }}>🔔 Notifications</span>
            <button
              onClick={load}
              disabled={loading}
              style={{
                background: 'transparent', border: 'none', color: '#888',
                fontSize: 11, cursor: loading ? 'wait' : 'pointer',
              }}
              title="Refresh"
            >{loading ? '…' : '↻'}</button>
          </div>

          {data.items.length === 0 ? (
            <div style={{ padding: '20px 8px', textAlign: 'center', color: '#666', fontSize: 12 }}>
              No new notifications
            </div>
          ) : (
            data.items.map(item => (
              <button
                key={item.id}
                onClick={() => handleClickItem(item)}
                style={{
                  display: 'flex',
                  width: '100%',
                  gap: 8,
                  alignItems: 'flex-start',
                  background: 'transparent',
                  border: '1px solid transparent',
                  borderRadius: 4,
                  padding: '8px',
                  textAlign: 'left',
                  cursor: item.link ? 'pointer' : 'default',
                  color: '#dcd0a0',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(201,168,76,0.08)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontSize: 16, flexShrink: 0 }}>{item.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 12, color: '#dcd0a0', fontWeight: 500 }}>{item.title}</span>
                  {item.detail && (
                    <span style={{ display: 'block', fontSize: 10, color: '#888', marginTop: 2 }}>{item.detail}</span>
                  )}
                </span>
                <span style={{ fontSize: 9, color: '#666', flexShrink: 0, marginTop: 2 }}>{fmtAgo(item.createdAt)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
